import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import UserFilesContext from '../../contexts/data/users/UserFilesContext';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { Table, Button, Space, Popconfirm, message, Flex, Typography, Pagination, Tooltip } from 'antd';
import { DownloadOutlined, ReloadOutlined, DeleteOutlined, UploadOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import UserDoc from '../../../domain/models/UserDoc';
import { IUserFile } from '@leedpulse-monorepo/interfaces';
import deleteUserFiles from '../../../domain/use_cases/users/deleteUserFiles';
import downloadUserFile from '../../../domain/use_cases/users/downloadUserFile';
import downloadUsersFiles from '../../../domain/use_cases/users/downloadUsersFiles';
import UserFileUploadModal from './user_files/UserFileUploadModal';
import UserFileDetailsModal from './user_files/UserFileDetailsModal';

const DESCRIPTION_MAX_LENGTH = 50;

interface UserFilesListProps {
    user: UserDoc;
}

export default function UserFilesList({ user }: UserFilesListProps) {
    const userFilesContext = useContext(UserFilesContext);
    const authContext = useContext(AuthenticationContext);

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [deleting, setDeleting] = useState(false);
    const [downloadingZip, setDownloadingZip] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [selectedUserFile, setSelectedUserFile] = React.useState<IUserFile | undefined>(undefined);
    const [searchParams, setSearchParams] = useSearchParams();
    const openedFileRef = useRef<string | null>(null);

    const handlePaginationChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        userFilesContext.fetch(user.uid, authContext.authentication, page, pageSize);
    };

    const handleRefresh = () => {
        setCurrentPage(1);
        setSelectedRowKeys([]);
        userFilesContext.fetch(user.uid, authContext.authentication);
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteUserFiles(user.uid, selectedRowKeys as string[], authContext.authentication);
            message.success('Fichiers supprimés avec succès');
            setSelectedRowKeys([]);
            setCurrentPage(1);
            userFilesContext.fetch(user.uid, authContext.authentication);
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors de la suppression');
        } finally {
            setDeleting(false);
        }
    };

    const handleUpdateCallback = () => {
        setCurrentPage(1);
        userFilesContext.fetch(user.uid, authContext.authentication);
        setSelectedUserFile(undefined);
        setIsUploadModalOpen(false);
    };

    const handleDownloadZip = async () => {
        setDownloadingZip(true);
        try {
            const { url, filename } = await downloadUsersFiles(user.uid, selectedRowKeys as string[], authContext.authentication);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors du téléchargement');
        } finally {
            setDownloadingZip(false);
        }
    };

    const handleDownload = async (record: IUserFile) => {
        try {
            const { url, filename } = await downloadUserFile(user.uid, record, authContext.authentication);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors du téléchargement');
        }
    };

    const handleDeleteFile = async (fileId: string) => {
        try {
            await deleteUserFiles(user.uid, [fileId], authContext.authentication);
            message.success('Fichier supprimé avec succès');
            setSelectedRowKeys((keys) => keys.filter((k) => k !== fileId));
            userFilesContext.fetch(user.uid, authContext.authentication, currentPage);
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors de la suppression');
        }
    };

    const handleCancelDetailsModal = () => {
        setSelectedUserFile(undefined);
        searchParams.delete('file');
        setSearchParams(searchParams, { replace: true });
    };

    useEffect(() => {
        if (
            userFilesContext.status === ContextStatus.IDLE ||
            userFilesContext.scopedUserUid !== user.uid
        ) {
            userFilesContext.fetch(user.uid, authContext.authentication);
        }
        return () => {
            userFilesContext.reset();
        };
    }, [user.uid]);

    useEffect(() => {
        const fileId = searchParams.get('file');
        if (!fileId || openedFileRef.current === fileId) return;
        const file = userFilesContext.items.find((f) => f.id === fileId);
        if (file) {
            openedFileRef.current = fileId;
            // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
            // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
            // external system") ; le setState synchrone est le reset qui precede l'appel.
            // eslint-disable-next-line react/set-state-in-effect
            setSelectedUserFile(file);
        }
    }, [searchParams, userFilesContext.items]);

    const columns = [
        {
            title: 'Type',
            dataIndex: 'mime_type',
            key: 'mime_type',
            render: (mime_type: IUserFile['mime_type']) => mime_type,
        },
        {
            title: 'Nom',
            dataIndex: 'name',
            key: 'name',
            render: (_: unknown, record: IUserFile) => (
                <Space size="small">
                    {record.sensitive && (
                        <Tooltip title="Fichier sensible">
                            <EyeInvisibleOutlined style={{ color: '#cf1322' }} />
                        </Tooltip>
                    )}
                    {record.name}
                </Space>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (description: IUserFile['description']) => <Typography.Text ellipsis>{description}</Typography.Text>,
        },
        {
            title: 'Date',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (timestamp: number) => moment(timestamp).format('DD/MM/YYYY à HH:mm'),
        },
        {
            title: '',
            key: 'actions',
            render: (record: IUserFile) =>
                <Flex gap="small" justify="end">
                    <Button type="primary" onClick={() => handleDownload(record)} icon={<DownloadOutlined />} />

                    <Popconfirm
                        title="Supprimer le fichier"
                        description="Cette action est irréversible. Confirmer la suppression ?"
                        onConfirm={() => handleDeleteFile(record.id)}
                        okText="Supprimer"
                        cancelText="Annuler"
                    >
                        <Button danger type="primary" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Flex>
        },
    ];

    return (
        <div className="list-container">
            <div className="list-container__controls">
                <Flex gap="small" align='baseline'>
                    <Typography.Title level={5}>Fichiers ({userFilesContext.count})</Typography.Title>
                </Flex>
                <Space>
                    <Button onClick={() => setIsUploadModalOpen(true)} icon={<UploadOutlined />}>
                        Téléverser des fichiers
                    </Button>
                    <Button type="primary" onClick={handleRefresh} icon={<ReloadOutlined />}>
                        Actualiser
                    </Button>
                </Space>
            </div>

            <Flex justify="space-between" align="baseline" style={{ marginBottom: "1rem" }}>
                <Flex vertical gap="small">
                    {selectedRowKeys.length > 0 ? <>
                        <Flex align="baseline" gap="small">
                            <Button
                                icon={<DownloadOutlined />}
                                loading={downloadingZip}
                                onClick={handleDownloadZip}
                            >
                                Télécharger
                            </Button>
                            <Popconfirm
                                title="Supprimer les fichiers sélectionnés"
                                description="Cette action est irréversible. Confirmer la suppression ?"
                                onConfirm={handleDelete}
                                okText="Supprimer"
                                cancelText="Annuler"
                                disabled={selectedRowKeys.length === 0}
                            >
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={deleting}
                                    disabled={selectedRowKeys.length === 0}
                                >
                                    Supprimer
                                </Button>
                            </Popconfirm>
                        </Flex>
                        <h4 style={{ marginLeft: '0.5rem', marginBottom: '0rem' }}>
                            {selectedRowKeys.length} fichier(s) sélectionné(s)
                        </h4>
                    </> : <div />}
                </Flex>

                <Pagination
                    current={currentPage}
                    total={userFilesContext.count}
                    pageSize={userFilesContext.hitsPerPage}
                    onChange={handlePaginationChange}
                    showSizeChanger={true}
                    pageSizeOptions={[20, 50, 100, 200]}
                    disabled={userFilesContext.status === ContextStatus.LOADING}
                />
            </Flex>

            <Table
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                }}
                onRow={(record) => ({
                    onClick: (e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('button') || target.closest('.ant-table-selection-column') || target.closest('.ant-checkbox-wrapper')) return;
                        setSelectedUserFile(record);
                    },
                    style: { cursor: 'pointer' },
                })}
                pagination={{
                    current: currentPage,
                    total: userFilesContext.count,
                    pageSize: userFilesContext.hitsPerPage,
                    onChange: handlePaginationChange,
                    showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100, 200],
                    position: ['bottomRight'],
                    disabled: userFilesContext.status === ContextStatus.LOADING,
                }}
                loading={userFilesContext.status === ContextStatus.LOADING}
                columns={columns}
                rowKey={(record) => record.id}
                dataSource={userFilesContext.items}
            />

            <UserFileUploadModal
                open={isUploadModalOpen}
                onCancel={() => setIsUploadModalOpen(false)}
                userDoc={user}
                onUpdateCallback={handleUpdateCallback}
                maxDescriptionLength={DESCRIPTION_MAX_LENGTH}
            />

            <UserFileDetailsModal
                userFile={selectedUserFile}
                onCancel={handleCancelDetailsModal}
                userDoc={user}
                onUpdateCallback={handleUpdateCallback}
                maxDescriptionLength={DESCRIPTION_MAX_LENGTH}
                downloadingZip={downloadingZip}
                onDownloadZip={handleDownload}
            />
        </div>
    );
}
